import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function SubscribeForm({
  title,
  description,
  endpoint,
  extra = {},
  icon: Icon,
  accent = "sage",
  testid = "subscribe",
}) {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const isCrisis = accent === "amber";
  const iconWrap = isCrisis ? "bg-amber-100 text-amber-700" : "bg-sage/15 text-sage-dark";
  const btnClass = isCrisis
    ? "bg-amber-600 text-white hover:bg-amber-700"
    : "bg-ink text-white hover:bg-sage-dark";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post(endpoint, { email: email.trim(), ...extra });
      if (res.data.already) {
        toast.info(t("sub.already"));
      } else {
        toast.success(t("sub.done"));
        setDone(true);
        setEmail("");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("sub.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`${testid}-card`}
      className={`p-6 sm:p-7 rounded-sm border ${isCrisis ? "border-amber-200 bg-amber-50/40" : "border-border bg-white"}`}
    >
      <div className="flex items-start gap-3">
        {Icon && (
          <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-sm ${iconWrap}`}>
            <Icon className="h-5 w-5" strokeWidth={2} />
          </span>
        )}
        <h3 className="font-heading text-xl sm:text-2xl text-ink leading-tight">{title}</h3>
      </div>
      <p className="mt-4 text-sm text-gray-700 leading-relaxed">{description}</p>

      {done ? (
        <div className="mt-5 flex items-center gap-2 text-sm font-semibold text-sage-dark" data-testid={`${testid}-success`}>
          <Check className="h-4 w-4" /> {t("sub.success")}
          <button onClick={() => setDone(false)} className="ml-2 underline text-gray-400 hover:text-ink font-normal">
            {t("sub.addAnother")}
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2.5">
          <Input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("sub.placeholder")}
            data-testid={`${testid}-input`}
            className="h-11 rounded-sm font-data text-sm focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage"
          />
          <Button
            type="submit"
            disabled={loading}
            data-testid={`${testid}-button`}
            className={`h-11 px-5 rounded-sm font-semibold shrink-0 ${btnClass}`}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("sub.button")}
          </Button>
        </form>
      )}
    </div>
  );
}
